import { useEffect, useMemo, useRef, useState } from "react";
import AIIcon from "@/assets/summary/ai.svg?react";
import RetryIcon from "@/assets/summary/retry.svg?react";
import EditIcon from "@/assets/summary/edit.svg?react";
import SummaryPanel from "./SummaryPanel";
import Chapter from "./Chapter";
import summaryCommonStore from "@/store/summaryCommon.store";
import SpeakerSummary from "./SpeakerSummary";
import EmptyPanel from "./EmptyPanel";
import Button from "@/component/Button";
import SuccessCircleIcon from "@/assets/common/ic_success_circle.svg?react";
import CloseIcon from "@/assets/common/ic_close_outline.svg?react";
import SuccessOutlineIcon from "@/assets/common/ic_success_outline.svg?react";
import summarySrcStore from "@/store/summarySrc.store";
import summaryStore from "@/store/summary.store";
import summarySpeakerStore from "@/store/summarySpeaker.store";
import summaryChapterStore from "@/store/summaryChapter.store";
import usePermission from "@/sections/Summary/usePermission";
import { message } from "antd";
import globalStore from "@/store/global.store";
import Tooltip from "@/component/Tooltip";
import recordStore from "@/store/record.store";
import fridayReporter from "@/utils/friday";
import { useShallow } from "zustand/react/shallow";
import { tabList, Tab } from "@/constants/record";
import { IS_PRIVATE } from '@/config';


const SummaryContent = () => {
  const { activeTab, summaryEditing, summaryEditingFinish, streamId, retryStatus } =
    summaryCommonStore(
      useShallow((state) => ({
        activeTab: state.activeTab,
        summaryEditing: state.summaryEditing,
        summaryEditingFinish: state.summaryEditingFinish,
        shareInfo: state.shareInfo,
        streamId: state.streamId,
        retryStatus: state.retryStatus,
      }))
    );
  const detailInfo = summarySrcStore((state) => state.detailInfo);
  const summaryStreamsAllDone = summaryStore((state) => state.summaryStreamsAllDone);
  const retryStatusRef = useRef(retryStatus);
  retryStatusRef.current = retryStatus;

  const records = recordStore((state) => state.records);

  const { setActiveTab, setSummaryEditing, submitSummaryEdit, setRetryStatus, shareInfo, isOnlyOriginal, setIsOnlyOriginal } =
    summaryCommonStore.getState();
  const {
    fetchSummary,
    fetchSummaryStreams,
    initNewSummary,
    setLastSummary,
    resetLastSummary,
  } = summaryStore.getState();
  const { fetchChapterInfo } = summaryChapterStore.getState();
  const { initNewChapterInfo, setLastChapterInfo, resetLastChapterInfo } =
    summaryChapterStore.getState();
  const {
    fetchSpeakerSummary,
    initNewSpeakerSummary,
    setLastSpeakerSummary,
    resetLastSpeakerSummary,
  } = summarySpeakerStore.getState();

  const isSrcDocExpand = globalStore((state) => state.isSrcDocExpand);

  const { isOwner, allowBackUp, showEdit } = usePermission();
  const activeTabItem = tabList.find((item) => item.id === activeTab);
  const [isFetchingAll, setIsFetchingAll] = useState(false);


  useEffect(() => {
    const handleCopy = function () {
      fridayReporter.pushData({
        eventCode: "SummaryPage_Summary_Copy",
        payload: {
          text_type: activeTabItem?.value,
        },
      });
    };
    document.addEventListener("copy", handleCopy);
    return () => {
      document.removeEventListener("copy", handleCopy);
    };
  }, [activeTabItem]);

  const scrollingRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<Tab>();
  activeTabRef.current = activeTab;

  const fetchAllSummary = async (
    params: {
      streamName: string;
      reload: boolean;
      contentStep: string;
      cover: boolean;
    },
  ) => {
    try {
      setIsFetchingAll(true);
      setRetryStatus(1);
      retryStatusRef.current = 1;
      
      // 保存当前状态作为备份
      setLastSummary(summaryStore.getState().summary);
      setLastChapterInfo(summaryChapterStore.getState().chapterInfo);
      setLastSpeakerSummary(summarySpeakerStore.getState().speakerSummary);

      const chapterPromise = fetchChapterInfo(params);
      const speakerPromise = fetchSpeakerSummary(params);
      const summaryPromise = new Promise<void>((resolve) => {
        fetchSummaryStreams(params, () => {
          resolve();
        });
      });
      
      // 等待所有请求完成
      await Promise.all([
        chapterPromise,
        speakerPromise,
        summaryPromise,
      ]);
      
      // 所有请求完成后设置状态
      setRetryStatus(0);
      setIsFetchingAll(false);
      setIsOnlyOriginal(false);
      
    } catch (error) {
      console.error('获取总结失败:', error);
      setRetryStatus(0);
      setIsFetchingAll(false);
      message.error('获取总结失败，请重试');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleEmptyPanelClick = async () => {
    console.log("EmptyPanel clicked");
    
    if (isFetchingAll) {
      message.warning("正在获取总结，请稍后重试");
      return;
    }
    
    // 设置状态
    summaryCommonStore.getState().setShowBackTrace(false);
    summaryCommonStore.getState().setShowBackTraceEnter(false);
    
    const params = {
      streamName: `live/${streamId}`,
      reload: true,
      contentStep: "SRC_DOC",
      cover: false,
    };
    await fetchAllSummary(params);
  };

  const renderTab = useMemo(() => {
    if (isOnlyOriginal && retryStatusRef.current !== 1 && !isFetchingAll) {
      return <EmptyPanel onSummaryClick={handleEmptyPanelClick} />;
    }
    
    switch (activeTab) {
      case Tab.summary:
        return <SummaryPanel />;
      case Tab.smartChapters:
        return <Chapter />;
      case Tab.speakerSummary:
        return <SpeakerSummary />;
      default:
        return null;
    }
  }, [isOnlyOriginal, isFetchingAll, activeTab, handleEmptyPanelClick]);

  const handleClickTab = (id: Tab) => {
    let eventCode = "";
    switch (id) {
      case Tab.summary:
        eventCode = "SummaryPage_Summary_ViewSummary";
        break;
      case Tab.smartChapters:
        eventCode = "SummaryPage_Summary_ViewChapter";
        break;
      case Tab.speakerSummary:
        eventCode = "SummaryPage_Summary_ViewSpeaker";
        break;

      default:
        break;
    }
    if (eventCode) {
      fridayReporter.pushData({
        eventCode,
      });
    }

    if (summaryEditing) {
      message.warning("当前编辑内容未保存");
      return;
    }
    if (retryStatus === 1) {
      message.warning(isFetchingAll ? "正在获取总结，请稍后重试" : "重新生成中，请稍后重试");
      return;
    }
    if (retryStatus === 2) {
      message.warning("重新生成成功，请保存或放弃");
      return;
    }
    if (!detailInfo?.completeDocFinish) return;
    setActiveTab(id);
    scrollingRef.current?.scrollTo(0, 0);
  };

  const retryFetching = async (
    params: {
      streamName: string;
      reload: boolean;
      contentStep: string;
      cover: boolean;
    },
    fromCancelRetry = false
  ) => {
    const currentId = summaryCommonStore.getState().streamId;
    switch (activeTab) {
      case Tab.summary:
        setLastSummary(summaryStore.getState().summary);
        if (fromCancelRetry) {
          await fetchSummary(params);
        } else {
          fetchSummaryStreams(params, () => {
            if (currentId !== summaryCommonStore.getState().streamId) return;
            setRetryStatus(2);
          });
        }
        break;
      case Tab.smartChapters:
        setLastChapterInfo(summaryChapterStore.getState().chapterInfo);
        await fetchChapterInfo(params);
        break;
      case Tab.speakerSummary:
        setLastSpeakerSummary(summarySpeakerStore.getState().speakerSummary);
        await fetchSpeakerSummary(params);
        break;
      default:
    }
  };

  const handleRetry = async () => {
    
    fridayReporter.pushData({
      eventCode: "SummaryPage_Summary_Regenerate",
      payload: {
        text_type: activeTabItem?.value,
      },
    });
    if (!isOwner && allowBackUp) {
      message.warning("创建副本后可使用该功能");
      return;
    }
    const currentId = summaryCommonStore.getState().streamId;
    if (!detailInfo?.completeDocFinish) {
      message.warning("重新生成中，请稍后重试");
      return;
    }
    console.log('重新总结', retryStatusRef.current, retryStatus)
    if (retryStatusRef.current === 1) {
      message.warning("重新生成中，请稍后重试");
      return;
    }
    summaryCommonStore.getState().setShowBackTrace(false);
    summaryCommonStore.getState().setShowBackTraceEnter(false);
    setRetryStatus(1);
    retryStatusRef.current = 1;
    const params = {
      streamName: `live/${streamId}`,
      reload: true,
      contentStep: "SRC_DOC",
      cover: false,
    };
    await retryFetching(params);
    if (currentId !== summaryCommonStore.getState().streamId) return;
    // 走流接口
    if (activeTab !== Tab.summary || shareInfo) {
      setRetryStatus(2);
    }
    // setFullLoading(false);
  };

  const handleSaveRetry = async () => {
    fridayReporter.pushData({
      eventCode: "SummaryPage_Summary_Regenerate_Save",
      payload: {
        text_type: activeTabItem?.value,
      },
    });
    try {
      switch (activeTab) {
        case Tab.summary:
          initNewSummary();
          break;
        case Tab.smartChapters:
          initNewChapterInfo();
          break;
        case Tab.speakerSummary:
          initNewSpeakerSummary();
          break;
        default:
      }
      setRetryStatus(0);
      await submitSummaryEdit(true);
      message.success("保存成功");
    } catch (error) {
      message.error("保存失败");
    }
  };

  const handleCancelRetry = async () => {
    fridayReporter.pushData({
      eventCode: "SummaryPage_Summary_Regenerate_Giveup",
      payload: {
        text_type: activeTabItem?.value,
      },
    });
    summaryCommonStore.getState().setShowBackTraceEnter(true);
    switch (activeTab) {
      case Tab.summary:
        resetLastSummary();
        break;
      case Tab.smartChapters:
        resetLastChapterInfo();
        break;
      case Tab.speakerSummary:
        resetLastSpeakerSummary();
        break;
      default:
    }
    setRetryStatus(0);
  };

  const handleEdit = () => {
    fridayReporter.pushData({
      eventCode: "SummaryPage_Summary_Edit",
      payload: {
        text_type: activeTabItem?.value,
      },
    });
    if (!isOwner && allowBackUp) {
      message.warning("创建副本后可使用该功能");
      return;
    }
    if (retryStatus === 1) {
      message.warning("重新生成中，请稍后重试");
      return;
    }
    if (!detailInfo?.completeDocFinish) return;
    setSummaryEditing(true);
    summaryCommonStore.getState().setShowBackTrace(false);
  };

  const handleCancel = () => {
    setSummaryEditing(false);
  };

  const handleSaveEdit = () => {
    submitSummaryEdit();
  };

  return (
    <div
      className={`min-w-100 h-full flex-1 rounded-2.5 overflow-y-auto flex flex-col ${!isSrcDocExpand ? "mx-5" : ""
        } transition-margin duration-100 max-w-216 m-x-auto`}
    >
      <div className={`mt-5.75 px-8 flex items-center justify-between`}>
        <div
          className={`flex text-mx-tertiary relative ${detailInfo?.completeDocFinish ? "" : "opacity-30"
            }`}
        >
          {tabList.map((tab, index) => (
            <div
              key={tab.id}
              className={`text-4.5 position-relative cursor-pointer lh-6.5 ${index === 0 ? "ml-0" : "ml-10"
                } ${activeTab === tab.id && "font-600 text-mx-secondary"}`}
              onClick={() => handleClickTab(tab.id)}
            >
              {tab.value}
              {activeTab === tab.id && (
                <div className="z-1 absolute left-0 right-0 bottom--1.5 m-auto w-6 h-0.5 bg-gradient-to-r from-#A28CE2  to-#80B0FF rounded-25"></div>
              )}
              {activeTab === tab.id && (
                <AIIcon className="size-3.5 absolute right--4" />
              )}
            </div>
          ))}
          <div className="absolute left-0 right-0 bottom--1.5 h-.25 bg-#ECEEF3"></div>
        </div>
        {shareInfo ? (
          <div></div>
        ) : (summaryEditing && !summaryEditingFinish) ? (
          <div
            className={`w-32 justify-end flex flex-row gap-2 ${isSrcDocExpand ? "" : "mr-12"
              } transition-margin duration-75`}
          >
            <Button
              onClick={handleCancel}
              className="h-8 !p-y-2 !p-x-4 flex items-center"
            >
              取消
            </Button>
            <Button
              type="primary"
              onClick={handleSaveEdit}
              className="h-8 !p-y-2 !p-x-4 flex items-center"
            >
              完成
            </Button>
          </div>
        ) : records.length === 0 || isOnlyOriginal ? null : (
          <div
            className={`w-22 justify-end flex flex-row gap-5.5 ${isSrcDocExpand ? "" : "mr-10"
              } ${detailInfo?.completeDocFinish ? "" : "opacity-30"
              } transition-margin duration-75 h-8 items-center`}
          >
            {(isOwner || allowBackUp) && !IS_PRIVATE && (
              <Tooltip
                content={`${isOwner ? "重新生成" : "创建副本到我的会记可重新生成"
                  }`}
                hover
              >
                <RetryIcon className="w-4.5 h-4.5" onClick={handleRetry} />
              </Tooltip>
            )}
            {showEdit && summaryStreamsAllDone && (
              <Tooltip
                content={`${isOwner ? "编辑" : "创建副本到我的会记可进行编辑"}`}
                hover
              >
                <EditIcon className="w-4.5 h-4.5" onClick={handleEdit} />
              </Tooltip>
            )}
          </div>
        )}
      </div>
      {retryStatus === 2 && (
        <div className="w-full h-10 mt-2 bg-#fffbe8 flex flex-row items-center px-4 shrink-0">
          <SuccessCircleIcon className="size-4 mr-2" />
          <span className="text-mx-secondary text-3.5 flex-1">
            已重新生成会议总结
          </span>
          <Button
            className="flex items-center mr-2"
            onClick={handleCancelRetry}
          >
            <CloseIcon className="size-4 mr-1" />
            放弃
          </Button>
          <Button className="flex items-center" onClick={handleSaveRetry}>
            <SuccessOutlineIcon className="size-4 mr-1" />
            保存
          </Button>
        </div>
      )}
      <div
        ref={scrollingRef}
        className="pb-14 px-8 mt-6 overflow-auto flex-1 my-scrollbar"
      >
        {renderTab}
      </div>
      <div className="color-#CED2D9 text-2.5 font-400 h-6 lh-6 px-8">
        MAXHUB 领效智会大模型 引擎提供技术支持-Guangdong-shiyuan-20240314
      </div>
    </div>
  );
};

export default SummaryContent;
